import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme,
} from '@mui/material';
import { glass } from './glassTokens';

/**
 * GlassTable — TableContainer + Table with:
 *   - Glass surface on container only (not rows — perf)
 *   - Sticky translucent head
 *   - Zebra rows via rgba
 *   - Row hover glow
 *   - Top/bottom scroll-shadow edges
 *
 * Props:
 *   columns    — array of { label, align?, width? }
 *   children   — <TableBody> children (rows)
 *   stickyHeader — bool (default true)
 *   sx         — sx override for container
 */
export default function GlassTable({ columns = [], children, stickyHeader = true, sx = {}, ...rest }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        ...g.surface,
        // scroll shadow edges via pseudo-elements on a wrapper
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          height: 24,
          zIndex: 2,
          pointerEvents: 'none',
        },
        '&::before': {
          top: 0,
          background: isDark
            ? 'linear-gradient(to bottom, rgba(15,18,32,0.7) 0%, transparent 100%)'
            : 'linear-gradient(to bottom, rgba(246,247,251,0.7) 0%, transparent 100%)',
        },
        '&::after': {
          bottom: 0,
          background: isDark
            ? 'linear-gradient(to top, rgba(15,18,32,0.7) 0%, transparent 100%)'
            : 'linear-gradient(to top, rgba(246,247,251,0.7) 0%, transparent 100%)',
        },
        ...sx,
      }}
    >
      <TableContainer sx={{ maxHeight: 520, overflowX: 'auto' }}>
        <Table stickyHeader={stickyHeader} size="medium" {...rest}>
          {columns.length > 0 && (
            <TableHead>
              <TableRow>
                {columns.map((col, i) => (
                  <TableCell
                    key={i}
                    align={col.align || 'left'}
                    sx={{
                      width: col.width,
                      background: g.headBg,
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      borderBottom: `1px solid ${g.border}`,
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.65)',
                      py: 1.75,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody
            sx={{
              // Zebra via nth-child
              '& tr:nth-of-type(even) td': {
                background: g.subtleFill,
              },
              // Row hover glow
              '& tr:hover td': {
                background: g.rowHover,
                transition: 'background 0.15s ease',
              },
              '& td': {
                borderColor: g.border,
                color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
                fontSize: '0.875rem',
              },
            }}
          >
            {children}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
